import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useLocale, setMetaDescription, restoreBaselineHead } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";

function LegalShell({ title, updated, updatedLabel, children, testid, backLabel, seoKey }) {
  const { lang } = useLocale();
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t(`legal.${seoKey}.seo.title`);
    setMetaDescription(t(`legal.${seoKey}.seo.description`));
  }, [t, seoKey, lang]);

  useEffect(() => () => restoreBaselineHead(), []);

  return (
    <>
      <Nav />
      <main className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10" data-testid={testid}>
        <div className="max-w-3xl mx-auto">
          <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// LEGAL</p>
          <h1 className="font-anton text-5xl md:text-6xl mt-3">{title}</h1>
          <p className="font-mono text-[10px] tracking-[0.2em] text-white/50 mt-3">{updatedLabel} · {updated}</p>
          <div className="mt-10 space-y-6 text-white/75 leading-relaxed">{children}</div>
          <Link to={localisedTo("/", lang)} className="btn-ghost mt-12 inline-flex text-sm" data-testid="legal-back-link">{backLabel}</Link>
        </div>
      </main>
      <Footer />
    </>
  );
}

export function Privacy() {
  const { t } = useTranslation();
  return (
    <LegalShell
      seoKey="privacy"
      title={t("legal.privacy.title")}
      updated={t("legal.privacy.updated")}
      updatedLabel={t("legal.updatedLabel")}
      backLabel={t("legal.back")}
      testid="page-privacy"
    >
      <p>
        <Trans
          i18nKey="legal.privacy.intro"
          components={{ 1: <a href="https://djspyofficial.com" className="text-red-brand" /> }}
        />
      </p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.privacy.collectHeading")}</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <Trans i18nKey="legal.privacy.collectAccount" components={{ b: <b /> }} />
        </li>
        <li>
          <Trans i18nKey="legal.privacy.collectPurchase" components={{ b: <b /> }} />
        </li>
        <li>
          <Trans i18nKey="legal.privacy.collectAnalytics" components={{ b: <b /> }} />
        </li>
      </ul>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.privacy.useHeading")}</h2>
      <p>{t("legal.privacy.useBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.privacy.rightsHeading")}</h2>
      <p>
        <Trans
          i18nKey="legal.privacy.rightsBody"
          components={{ 1: <a href="mailto:bookings@djspyofficial.com" className="text-red-brand" /> }}
        />
      </p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.privacy.processorsHeading")}</h2>
      <p>{t("legal.privacy.processorsBody")}</p>
    </LegalShell>
  );
}

export function Terms() {
  const { t } = useTranslation();
  return (
    <LegalShell
      seoKey="terms"
      title={t("legal.terms.title")}
      updated={t("legal.terms.updated")}
      updatedLabel={t("legal.updatedLabel")}
      backLabel={t("legal.back")}
      testid="page-terms"
    >
      <p>{t("legal.terms.intro")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.terms.accountHeading")}</h2>
      <p>{t("legal.terms.accountBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.terms.purchasesHeading")}</h2>
      <p>{t("legal.terms.purchasesBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4" id="currency" data-testid="terms-currency-heading">{t("legal.terms.currencyHeading")}</h2>
      <p data-testid="terms-currency-body">{t("legal.terms.currencyBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.terms.licenseHeading")}</h2>
      <p>{t("legal.terms.licenseBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.terms.prohibitedHeading")}</h2>
      <p>{t("legal.terms.prohibitedBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.terms.liabilityHeading")}</h2>
      <p>{t("legal.terms.liabilityBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4" data-testid="terms-trademark-heading">{t("legal.terms.trademarkHeading")}</h2>
      <p data-testid="terms-trademark-body" className="whitespace-pre-line">{t("legal.terms.trademarkBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.terms.contactHeading")}</h2>
      <p><a href="mailto:bookings@djspyofficial.com" className="text-red-brand">bookings@djspyofficial.com</a></p>
    </LegalShell>
  );
}

export function Refund() {
  const { t } = useTranslation();
  return (
    <LegalShell
      seoKey="refund"
      title={t("legal.refund.title")}
      updated={t("legal.refund.updated")}
      updatedLabel={t("legal.updatedLabel")}
      backLabel={t("legal.back")}
      testid="page-refund"
    >
      <div className="hairline rounded-2xl bg-red-brand/10 border-red-brand p-6">
        <p className="text-white leading-relaxed">{t("legal.refund.summary")}</p>
      </div>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.refund.previewHeading")}</h2>
      <p>{t("legal.refund.previewBody")}</p>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.refund.howHeading")}</h2>
      <ol className="list-decimal pl-5 space-y-2">
        <li>
          <Trans
            i18nKey="legal.refund.step1"
            components={{ 1: <a href="mailto:bookings@djspyofficial.com" className="text-red-brand" /> }}
          />
        </li>
        <li>{t("legal.refund.step2")}</li>
        <li>
          <Trans i18nKey="legal.refund.step3" components={{ b: <b /> }} />
        </li>
        <li>{t("legal.refund.step4")}</li>
      </ol>
      <h2 className="font-display text-2xl text-white pt-4">{t("legal.refund.statutoryHeading")}</h2>
      <p>{t("legal.refund.statutoryBody")}</p>
    </LegalShell>
  );
}
